import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, CreditCard, Tag, Check, X, Truck, Globe } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const FREE_SHIP_THRESHOLD = 150;

const PayPalIcon = () => (
  <svg width="18" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20.067 8.478c.492.315.844.826.968 1.512.13.688.037 1.53-.283 2.507-.808 2.478-2.803 3.68-5.797 3.68h-.61a.756.756 0 0 0-.746.635l-.03.183-.717 4.54-.024.129a.377.377 0 0 1-.373.319H9.36a.377.377 0 0 1-.373-.436l1.916-12.14a.756.756 0 0 1 .746-.635h4.09c1.5 0 2.702.32 3.5 1.11.35.348.596.75.828 1.196z"/>
    <path d="M7.83 4.29a.756.756 0 0 1 .746-.635h4.09c.575 0 1.11.037 1.594.115a5.913 5.913 0 0 1 1.35.343 3.677 3.677 0 0 1 1.036.6c.318.264.586.575.803.937.216.362.377.775.483 1.24.106.462.16.984.16 1.565 0 .61-.06 1.16-.183 1.65-.121.49-.31.94-.564 1.348-.253.406-.567.762-.94 1.067-.375.305-.812.556-1.313.752a5.99 5.99 0 0 1-1.665.34c-.58.055-1.208.082-1.885.082h-1.207a.939.939 0 0 0-.928.792l-.72 4.564-.256 1.615a.377.377 0 0 1-.373.319H3.982a.377.377 0 0 1-.373-.436L5.523 6.404 7.83 4.29z" opacity=".55"/>
  </svg>
);

export default function Checkout() {
  const { items, subtotal, count } = useCart();
  const [email, setEmail] = useState(localStorage.getItem("davide_shopper_email") || "");
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null); // {valid, percent_off, discount_amount, final_total}
  const [validating, setValidating] = useState(false);
  const [loading, setLoading] = useState(null);

  const applyCode = async () => {
    if (!code.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.post("/discounts/validate", { code: code.trim(), subtotal });
      if (data.valid) {
        setDiscount(data);
        toast.success(`${data.percent_off}% off applied`);
      } else {
        setDiscount(null);
        toast.error("Invalid or expired code");
      }
    } catch {
      toast.error("Could not validate code");
    } finally { setValidating(false); }
  };

  const clearCode = () => { setDiscount(null); setCode(""); };

  const total = discount?.valid ? discount.final_total : subtotal;
  const savings = discount?.valid ? discount.discount_amount : 0;
  const freeShip = subtotal >= FREE_SHIP_THRESHOLD;
  const remaining = Math.max(0, FREE_SHIP_THRESHOLD - subtotal);

  const persistEmail = (e) => {
    setEmail(e);
    if (e) localStorage.setItem("davide_shopper_email", e);
  };

  const payWithStripe = async () => {
    if (items.length === 0) return;
    setLoading("stripe");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        email: email || undefined,
        discount_code: discount?.valid ? discount.code : undefined,
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data?.url) window.location.href = data.url;
      else toast.error("Unable to start Stripe checkout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe checkout failed");
    } finally { setLoading(null); }
  };

  const payWithPayPal = async () => {
    if (items.length === 0) return;
    setLoading("paypal");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        email: email || undefined,
        discount_code: discount?.valid ? discount.code : undefined,
      };
      const { data } = await api.post("/paypal/create-order", payload);
      if (data?.approve_url) {
        sessionStorage.setItem("davide_paypal_order_id", data.order_id);
        window.location.href = data.approve_url;
      } else {
        toast.error("Unable to start PayPal checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal checkout failed");
    } finally { setLoading(null); }
  };

  const payWithPaystack = async () => {
    if (items.length === 0) return;
    if (!email.trim()) { toast.error("Paystack requires an email address"); return; }
    setLoading("paystack");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size })),
        origin_url: window.location.origin,
        email: email,
        discount_code: discount?.valid ? discount.code : undefined,
      };
      const { data } = await api.post("/paystack/initialize", payload);
      if (data?.authorization_url) {
        sessionStorage.setItem("davide_paystack_ref", data.reference);
        toast.success(`Redirecting — ₦${data.total_ngn.toLocaleString()}`);
        window.location.href = data.authorization_url;
      } else {
        toast.error("Unable to start Paystack checkout");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Paystack checkout failed");
    } finally { setLoading(null); }
  };

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 min-h-screen flex items-center justify-center px-6" data-testid="checkout-empty-page">
        <div className="text-center">
          <ShoppingBag size={48} className="text-white/40 mx-auto mb-6" />
          <h1 className="font-display text-4xl text-white">Your cart is empty.</h1>
          <p className="text-white/60 mt-3">Add a few pieces before you check out.</p>
          <Link to="/shop" className="btn-gold inline-flex mt-8" data-testid="checkout-empty-shop-btn">Browse Shop</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <Link to="/shop" className="text-sm uppercase tracking-widest text-white/60 hover:text-[var(--davide-gold)] inline-flex items-center gap-2 mb-8" data-testid="checkout-back-link">
          <ArrowLeft size={14} /> Continue Shopping
        </Link>

        <p className="overline mb-4">Final Step</p>
        <h1 className="font-display text-5xl md:text-6xl font-light text-white tracking-tighter">Checkout.</h1>
        <p className="text-white/60 mt-3">{count} {count === 1 ? "item" : "items"} · Choose your payment method.</p>

        <div className="grid lg:grid-cols-3 gap-10 mt-14">
          {/* Order Summary */}
          <div className="lg:col-span-2 space-y-5 border border-white/10 bg-[#101010] p-6 md:p-8" data-testid="order-summary">
            <div className="overline mb-2">Your Order</div>
            {items.map((i) => (
              <div key={i.key} className="flex gap-4 py-3 border-b border-white/5 last:border-0" data-testid={`summary-item-${i.product_id}`}>
                <img src={i.image_url} alt={i.name} className="w-20 h-24 object-cover" />
                <div className="flex-1">
                  <p className="text-white font-medium">{i.name}</p>
                  <p className="text-white/50 text-xs mt-1">
                    {i.size && <>Size: {i.size} · </>}Qty: {i.quantity}
                  </p>
                </div>
                <p className="text-[var(--davide-gold)] font-display text-lg">
                  ${(i.price * i.quantity).toFixed(2)}
                </p>
              </div>
            ))}

            <div className="pt-4 space-y-2 text-sm">
              <div className="flex justify-between text-white/70">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              {savings > 0 && (
                <div className="flex justify-between text-[var(--davide-gold)]" data-testid="checkout-savings">
                  <span>Discount ({discount.code})</span>
                  <span>− ${savings.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-white/70">
                <span className="inline-flex items-center gap-2"><Truck size={14}/> Shipping</span>
                <span>{freeShip ? <span className="text-[var(--davide-gold)]">FREE</span> : "Calculated at payment"}</span>
              </div>
            </div>

            <div className="flex justify-between items-baseline pt-3 border-t border-white/15">
              <span className="overline">Total</span>
              <span className="font-display text-4xl text-[var(--davide-gold)]" data-testid="checkout-total">
                ${total.toFixed(2)}
              </span>
            </div>

            {!freeShip && (
              <p className="text-xs text-white/50" data-testid="ship-nudge">
                Add ${remaining.toFixed(2)} more to unlock free worldwide shipping.
              </p>
            )}
            <p className="text-xs text-white/40">A portion of every sale funds YAAIDFI.</p>
          </div>

          {/* Payment */}
          <div className="space-y-6" data-testid="payment-methods">
            {/* Email */}
            <div>
              <label className="overline block mb-2" style={{color:"var(--davide-muted)"}}>Email (for receipt & order tracking)</label>
              <input type="email" value={email} onChange={(e)=>persistEmail(e.target.value)}
                data-testid="checkout-email-input"
                placeholder="you@example.com"
                className="w-full bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[var(--davide-gold)] outline-none transition-colors" />
            </div>

            {/* Discount code */}
            <div>
              <label className="overline block mb-2" style={{color:"var(--davide-muted)"}}>Discount Code</label>
              {!discount?.valid ? (
                <div className="flex gap-2">
                  <input value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())}
                    data-testid="discount-code-input"
                    placeholder="WELCOME10"
                    className="flex-1 bg-transparent border border-white/20 px-4 py-3 text-white focus:border-[var(--davide-gold)] outline-none transition-colors uppercase tracking-widest text-sm" />
                  <button onClick={applyCode} disabled={validating || !code.trim()}
                    data-testid="apply-discount-btn"
                    className="btn-outline-gold" style={{padding: "0.7rem 1.2rem"}}>
                    <Tag size={14}/> {validating ? "…" : "Apply"}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between border border-[var(--davide-gold)]/40 bg-[var(--davide-gold)]/5 px-4 py-3" data-testid="discount-applied">
                  <span className="inline-flex items-center gap-2 text-[var(--davide-gold)] font-medium text-sm">
                    <Check size={16}/> {discount.code} · {discount.percent_off}% off
                  </span>
                  <button onClick={clearCode} className="text-white/60 hover:text-white" data-testid="clear-discount-btn"><X size={16}/></button>
                </div>
              )}
              <p className="text-[10px] text-white/40 mt-2 tracking-widest uppercase">Try WELCOME10 for 10% off</p>
            </div>

            {/* Pay */}
            <div>
              <div className="overline mb-3">Pay With</div>
              <div className="space-y-3">
                <button onClick={payWithStripe} disabled={loading !== null}
                  data-testid="pay-stripe-btn"
                  className="btn-gold w-full justify-center">
                  <CreditCard size={16} />
                  {loading === "stripe" ? "Redirecting…" : "Pay with Card (Stripe)"}
                </button>

                <div className="flex items-center gap-3 text-white/30 text-xs uppercase tracking-widest py-1">
                  <span className="h-px flex-1 bg-white/10" /> or <span className="h-px flex-1 bg-white/10" />
                </div>

                <button onClick={payWithPayPal} disabled={loading !== null}
                  data-testid="pay-paypal-btn"
                  className="btn-outline-gold w-full justify-center inline-flex items-center gap-3"
                  style={{padding: "1rem 1.5rem"}}>
                  <PayPalIcon />
                  {loading === "paypal" ? "Redirecting…" : "Pay with PayPal"}
                </button>

                <button onClick={payWithPaystack} disabled={loading !== null}
                  data-testid="pay-paystack-btn"
                  className="w-full justify-center inline-flex items-center gap-3 border transition-all duration-300"
                  style={{
                    padding: "1rem 1.5rem",
                    borderColor: "rgba(0, 194, 154, 0.7)",
                    color: "#00c29a",
                    background: "rgba(0, 194, 154, 0.06)",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.18em",
                    cursor: loading !== null ? "not-allowed" : "pointer",
                  }}>
                  <Globe size={16} />
                  {loading === "paystack" ? "Redirecting…" : "Pay with Paystack (Africa)"}
                </button>
              </div>
              <p className="text-[11px] text-white/40 text-center pt-4 leading-relaxed">
                Secure checkout via Stripe, PayPal, or Paystack. Paystack charges in NGN for African customers — you'll return here after payment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
